import React from "react";
import Icon from "components/AppIcon";

const INVESTOR_TYPES = [
  {
    value: "fund_manager",
    label: "Fund Manager",
    description: "Regulated investment management firm managing pooled capital on behalf of clients",
    icon: "TrendingUp",
    examples: "Private equity, infrastructure funds, asset managers",
  },
  {
    value: "family_office",
    label: "Family Office",
    description: "Private wealth management advisory firm serving ultra-high-net-worth families",
    icon: "Home",
    examples: "Single family office, multi-family office",
  },
  {
    value: "sovereign_wealth",
    label: "Sovereign Wealth Fund",
    description: "State-owned investment fund or entity investing national savings or reserves",
    icon: "Landmark",
    examples: "National investment authority, government pension reserve",
  },
  {
    value: "dfi",
    label: "Development Finance Institution",
    description: "Specialised development bank or finance institution supporting private sector development",
    icon: "Globe",
    examples: "CDC Group, DEG, Proparco, FMO",
  },
  {
    value: "pension_fund",
    label: "Pension Fund",
    description: "Institutional investor managing retirement savings with long-term liability matching",
    icon: "Shield",
    examples: "Defined benefit scheme, occupational pension fund",
  },
  {
    value: "insurance",
    label: "Insurance Company",
    description: "Regulated insurance or reinsurance entity investing premium reserves",
    icon: "FileCheck",
    examples: "Life insurer, general insurer, Lloyd's syndicate",
  },
  {
    value: "endowment",
    label: "Endowment / Foundation",
    description: "Non-profit or charitable entity managing long-term investment portfolio",
    icon: "BookOpen",
    examples: "University endowment, charitable foundation",
  },
  {
    value: "corporate",
    label: "Corporate Investor",
    description: "Corporate entity investing treasury or strategic capital in infrastructure",
    icon: "Building2",
    examples: "Corporate treasury, strategic investor",
  },
];

const AUM_RANGES = [
  { value: "under_100m", label: "Under £100m" },
  { value: "100m_500m", label: "£100m – £500m" },
  { value: "500m_1b", label: "£500m – £1bn" },
  { value: "1b_5b", label: "£1bn – £5bn" },
  { value: "5b_plus", label: "Over £5bn" },
];

export default function StepInvestorType({ data, onChange, errors }) {
  const handleTypeSelect = (value) => {
    onChange({ ...data, investorType: value });
  };

  const handleAumChange = (e) => {
    onChange({ ...data, aum: e?.target?.value });
  };

  const handleInfraExpChange = (e) => {
    onChange({ ...data, infraExperience: e?.target?.value });
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-caption text-muted-foreground mb-4">
          Select the category that best describes your organisation. This determines your access permissions and regulatory requirements on the platform.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {INVESTOR_TYPES?.map((type) => {
            const isSelected = data?.investorType === type?.value;
            return (
              <button
                key={type?.value}
                onClick={() => handleTypeSelect(type?.value)}
                className={`text-left p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                  isSelected
                    ? "border-primary bg-[rgba(59,76,76,0.07)]" :"border-border bg-card hover:border-secondary"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-md ${
                      isSelected ? "bg-primary" : "bg-muted"
                    }`}
                  >
                    <Icon
                      name={type?.icon}
                      size={18}
                      color={isSelected ? "white" : "var(--color-muted-foreground)"}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`font-caption font-semibold text-sm ${isSelected ? "text-primary" : "text-foreground"}`}>
                        {type?.label}
                      </p>
                      {isSelected && (
                        <Icon name="CheckCircle2" size={16} color="var(--color-primary)" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{type?.description}</p>
                    <p className="text-xs text-muted-foreground mt-1 italic">{type?.examples}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {errors?.investorType && (
          <p className="text-error text-xs mt-2">{errors?.investorType}</p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-1">
            Assets Under Management <span className="text-error">*</span>
          </label>
          <select
            value={data?.aum || ""}
            onChange={handleAumChange}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground font-caption text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select AUM range</option>
            {AUM_RANGES?.map((r) => (
              <option key={r?.value} value={r?.value}>{r?.label}</option>
            ))}
          </select>
          {errors?.aum && <p className="text-error text-xs mt-1">{errors?.aum}</p>}
        </div>

        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-1">
            Infrastructure Investment Experience
          </label>
          <select
            value={data?.infraExperience || ""}
            onChange={handleInfraExpChange}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground font-caption text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select experience level</option>
            <option value="none">No prior experience</option>
            <option value="under_3">Under 3 years</option>
            <option value="3_7">3–7 years</option>
            <option value="7_plus">Over 7 years</option>
          </select>
        </div>
      </div>
    </div>
  );
}